import { guid } from "@buildwithflux/shared";
// We use a symbol so we can still properly notify undefined as a value to subscribers
const notifiedValueNotYetReceived = Symbol.for("undefinedInitialValue");
/**
 * Wraps the concept of allowing callers to subscribe to changes in basic values.  It manages
 * the creation and release of callbacks that are to be fired when a value is updated, and also
 * the notification of listeners.
 */
export class SubscriptionManager {
    options;
    subscriptions;
    constructor(options = {
        sendLatestNotifiedValueOnSubscribe: true,
    }) {
        this.options = options;
        this.subscriptions = new Map();
    }
    /**
     * Bind a listener to notifications.  Optionally attach some custom cleanup code that will be
     * fired after a subscription is released.
     *
     * NOTE: The custom cleanup code will _not_ be run if a subscriber does not unsubscribe by
     * calling the unsusbscribe function returned by this function.  Should it be?
     */
    addSubscription(key, onUpdate, customCleanup) {
        const innerKey = guid();
        const existing = this.subscriptions.get(key);
        if (existing) {
            existing.inner.set(innerKey, onUpdate);
            if (this.options.sendLatestNotifiedValueOnSubscribe) {
                setTimeout(() => {
                    if (existing.latestNotifiedValue !== notifiedValueNotYetReceived) {
                        onUpdate(existing.latestNotifiedValue);
                    }
                }, 0);
            }
        }
        else {
            const inner = new Map();
            inner.set(innerKey, onUpdate);
            this.subscriptions.set(key, {
                inner,
                latestNotifiedValue: notifiedValueNotYetReceived,
            });
        }
        return this.createUnsubscriber(key, innerKey, customCleanup);
    }
    /**
     * Unsubscribe from updates.  This function is not intended to be called directly - instead,
     * it gets bound to the unsubscriber that is returned from addSubscription.
     *
     * NOTE: It's a little weird that this is public but not intended to be called publicly.
     */
    unsubscribe(key, innerKey) {
        const existing = this.subscriptions.get(key);
        if (!existing)
            return;
        existing.inner.delete(innerKey);
        // TODO: Should we remove the initialValue if there are no more listeners?
        // if (existing.inner.size === 0) {
        //     this.subscriptions.delete(key);
        // }
    }
    /**
     * Return the number of subscribers for a given key.
     */
    listenerCount(key) {
        const listeners = this.subscriptions.get(key);
        return listeners?.inner.size ?? 0;
    }
    /**
     * Notify all listeners of a certain subscriber key.
     */
    notify(key, update) {
        const subs = this.subscriptions.get(key);
        if (!subs)
            return;
        if (this.options.sendLatestNotifiedValueOnSubscribe) {
            subs.latestNotifiedValue = update;
        }
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        subs.inner.forEach((listener) => listener(update));
    }
    createUnsubscriber(key, innerKey, customCleanup) {
        return () => {
            this.unsubscribe(key, innerKey);
            if (customCleanup) {
                customCleanup();
            }
        };
    }
}
//# sourceMappingURL=subscriptionManager.js.map